// src/ui/screens/post/CreateAdScreen.jsx

import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, storage } from '../../../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';

const COLORS = {
  orange: '#FF8C08',
  bg: '#08090B',
  card: '#23262C',
  cardLight: '#2C2F36',
};

const AD_WIDTH = 443.8519;
const AD_HEIGHT = 179.4074;

// ── CreateAdScreen ────────────────────────────────────────────────────────────

export const CreateAdScreen = () => {
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [imageError, setImageError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const fileInputRef = useRef(null);

  // ── Form validation ──────────────────────────────────────────────────────

  const isFormValid = 
    selectedImage !== null &&
    title.trim().length > 0 &&
    description.trim().length > 0 &&
    linkUrl.trim().length > 0 &&
    !isUploadingImage &&
    !isSaving;

  // ── Image crop to ad dimensions ─────────────────────────────────────────

  const cropImageToAdDimensions = (file) => {
    return new Promise((resolve) => {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);
      img.onload = () => {
        const targetWidth = Math.round(AD_WIDTH);
        const targetHeight = Math.round(AD_HEIGHT);

        // Calculate scaling to cover target dimensions
        const scaleX = targetWidth / img.width;
        const scaleY = targetHeight / img.height;
        const scale = Math.max(scaleX, scaleY);

        const scaledWidth = Math.round(img.width * scale);
        const scaledHeight = Math.round(img.height * scale);

        const canvas = document.createElement('canvas');
        canvas.width = targetWidth;
        canvas.height = targetHeight;

        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        // Center crop
        const cropX = (scaledWidth - targetWidth) / 2;
        const cropY = (scaledHeight - targetHeight) / 2;

        ctx.drawImage(img, -cropX, -cropY, scaledWidth, scaledHeight);

        canvas.toBlob((blob) => {
          const croppedFile = new File([blob], file.name, { type: 'image/jpeg' });
          resolve(croppedFile);
        }, 'image/jpeg', 0.85);

        URL.revokeObjectURL(objectUrl);
      };
      img.src = objectUrl;
    });
  };

  // ── Pick image ────────────────────────────────────────────────────────────

  const handlePickImage = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploadingImage(true);
    setImageError(null);

    try {
      const croppedFile = await cropImageToAdDimensions(file);
      const previewUrl = URL.createObjectURL(croppedFile);
      setSelectedImage(previewUrl);
      setImageFile(croppedFile);
    } catch (error) {
      console.error('Image processing error:', error);
      setImageError('Failed to process image. Please try again.');
    } finally {
      setIsUploadingImage(false);
      e.target.value = '';
    }
  };

  const handleRemoveImage = () => {
    if (selectedImage) {
      URL.revokeObjectURL(selectedImage);
    }
    setSelectedImage(null);
    setImageFile(null);
  };

  // ── Upload image to Firebase Storage ─────────────────────────────────────

  const uploadImage = async () => {
    if (!imageFile) return null;

    try {
      const fileName = `ad_${Date.now()}.jpg`;
      const storageRef = ref(storage, `ads/${fileName}`);
      
      const task = uploadBytesResumable(storageRef, imageFile);
      await task;
      
      const downloadUrl = await getDownloadURL(storageRef);
      return downloadUrl;
    } catch (error) {
      console.error('Image upload error:', error);
      return null;
    }
  };

  // ── Save ad ───────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!isFormValid) {
      alert('Please fill all fields and select an image');
      return;
    }

    setIsSaving(true);

    try {
      const imageUrl = await uploadImage();
      if (!imageUrl) {
        throw new Error('Failed to upload image');
      }

      await addDoc(collection(db, 'ads'), {
        title: title.trim(),
        description: description.trim(),
        linkUrl: linkUrl.trim(),
        imageUrl: imageUrl,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        isActive: true,
        clickCount: 0,
        viewCount: 0,
      });

      alert('Ad created successfully!');
      navigate(-1);
    } catch (error) {
      console.error('Save ad error:', error);
      alert(`Failed to create ad: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  const screenWidth = window.innerWidth;
  const adHeight = Math.min(screenWidth * (AD_HEIGHT / AD_WIDTH), 200);

  return (
    <div className="min-h-screen bg-filatom-bg">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
      />

      {/* App Bar */}
      <div className="sticky top-0 z-10 bg-filatom-bg">
        <div className="flex items-center justify-between px-4 h-14">
          <button onClick={() => navigate(-1)} className="text-white p-2 -ml-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <span className="text-white text-lg font-semibold">Create Ad</span>
          <div className="w-9" />
        </div>
      </div>

      <div className="px-5 pb-8">
        {/* Ad Preview */}
        <div className="text-center">
          <span className="text-white/60 text-xs font-medium">Ad Preview</span>
          <div
            className="w-full mt-2 rounded-xl overflow-hidden"
            style={{
              height: adHeight,
              backgroundColor: COLORS.cardLight,
            }}
          >
            {selectedImage ? (
              <img
                src={selectedImage}
                alt="Ad preview"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center">
                <svg className="w-12 h-12 text-white/20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="text-white/30 text-xs mt-2">Ad Preview</span>
                <span className="text-white/20 text-[10px]">{Math.round(AD_WIDTH)} x {Math.round(AD_HEIGHT)}</span>
              </div>
            )}
          </div>
        </div>

        <div className="h-6" />

        {/* Image Picker */}
        <div className="p-4 rounded-xl" style={{ backgroundColor: COLORS.card }}>
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-filatom-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className="text-white/90 text-sm font-semibold">Ad Image</span>
            <span className="text-filatom-primary text-sm">*</span>
          </div>

          <button
            onClick={handlePickImage}
            className="w-full mt-2 p-4 rounded-xl border"
            style={{
              backgroundColor: COLORS.cardLight,
              borderColor: imageError ? '#FF1744' : 'rgba(255,255,255,0.07)',
            }}
          >
            {isUploadingImage ? (
              <div className="flex flex-col items-center gap-2">
                <div className="w-5 h-5 border-2 border-filatom-primary border-t-transparent rounded-full animate-spin" />
                <span className="text-white/50 text-xs">Processing image...</span>
              </div>
            ) : selectedImage ? (
              <div className="relative w-full h-24 rounded-lg overflow-hidden">
                <img src={selectedImage} alt="Selected" className="w-full h-full object-cover" />
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveImage();
                  }}
                  className="absolute top-2 right-2 w-6 h-6 rounded-full bg-black/80 flex items-center justify-center"
                >
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-1">
                <svg className="w-8 h-8 text-filatom-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                <span className="text-filatom-primary text-xs font-medium">Tap to select image</span>
                <span className="text-white/40 text-[10px]">Recommended: {Math.round(AD_WIDTH)} x {Math.round(AD_HEIGHT)}</span>
              </div>
            )}
          </button>

          {imageError && (
            <div className="text-red-500 text-xs mt-1">{imageError}</div>
          )}
        </div>

        <div className="h-4" />

        {/* Title */}
        <div className="p-4 rounded-xl" style={{ backgroundColor: COLORS.card }}>
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-filatom-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
            </svg>
            <span className="text-white/90 text-sm font-semibold">Ad Title</span>
            <span className="text-filatom-primary text-sm">*</span>
          </div>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter ad title (e.g., &quot;Special Offer 2024&quot;)"
            className="w-full mt-2 p-3.5 rounded-xl text-white text-sm outline-none placeholder:text-white/30"
            style={{ backgroundColor: COLORS.cardLight }}
          />
        </div>

        <div className="h-4" />

        {/* Description */}
        <div className="p-4 rounded-xl" style={{ backgroundColor: COLORS.card }}>
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-filatom-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            <span className="text-white/90 text-sm font-semibold">Ad Description</span>
            <span className="text-filatom-primary text-sm">*</span>
          </div>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter ad description (e.g., &quot;Get 50% off on your first booking&quot;)"
            rows={3}
            className="w-full mt-2 p-3.5 rounded-xl text-white text-sm outline-none placeholder:text-white/30 resize-none"
            style={{ backgroundColor: COLORS.cardLight }}
          />
        </div>

        <div className="h-4" />

        {/* Link */}
        <div className="p-4 rounded-xl" style={{ backgroundColor: COLORS.card }}>
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-filatom-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
            <span className="text-white/90 text-sm font-semibold">Ad Link (URL)</span>
            <span className="text-filatom-primary text-sm">*</span>
          </div>
          <input
            type="url"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            placeholder="https://example.com/offer"
            className="w-full mt-2 p-3.5 rounded-xl text-white text-sm outline-none placeholder:text-white/30"
            style={{ backgroundColor: COLORS.cardLight }}
          />
        </div>

        <div className="h-8" />

        {/* Save Button */}
        <button
          onClick={handleSave}
          disabled={!isFormValid || isSaving}
          className={`w-full h-13 rounded-xl text-black font-bold text-sm flex items-center justify-center gap-2.5 transition-all ${
            isFormValid && !isSaving
              ? 'bg-filatom-primary hover:opacity-90'
              : 'bg-filatom-primary/40 cursor-not-allowed'
          }`}
        >
          {isSaving ? (
            <div className="w-5.5 h-5.5 border-2 border-black border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <svg className="w-5.5 h-5.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              Publish Ad
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default CreateAdScreen;